/** @module @liquidefy/code-editor/language/language @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS — types and engine dependencies ═══

import type {
  LanguageId,
  LanguageDefinition,
  LanguageConfiguration,
  CommentRule,
  BracketPair,
  AutoClosingPair,
  SurroundingPair,
  FoldingRules,
  IndentationRules,
  OnEnterRule,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asLanguageId,
} from '../code-editor.js';

// ═══ §2 REGISTRY MANAGEMENT — CRUD operations on language definitions ═══

/**
 * Create a new language registry, optionally seeded with an initial set of definitions.
 */
export function createRegistry(languages?: readonly LanguageDefinition[]): LanguageDefinition[] {
  if (languages !== undefined && languages.length > 0) {
    return [...languages];
  }
  return [...BUILTIN_LANGUAGES];
}

/**
 * Register a new language definition in the registry.
 * Replaces an existing definition with the same id.
 */
export function registerLanguage(
  registry: readonly LanguageDefinition[],
  definition: LanguageDefinition,
): LanguageDefinition[] {
  const filtered = registry.filter((l) => l.id !== definition.id);
  return [...filtered, definition];
}

/**
 * Remove a language from the registry by id.
 */
export function unregisterLanguage(
  registry: readonly LanguageDefinition[],
  id: LanguageId,
): LanguageDefinition[] {
  return registry.filter((l) => l.id !== id);
}

/**
 * Get a language definition by its id.
 */
export function getLanguage(
  registry: readonly LanguageDefinition[],
  id: LanguageId,
): LanguageDefinition | null {
  return registry.find((l) => l.id === id) ?? null;
}

/**
 * Look up a language by file extension (with or without leading dot).
 */
export function getLanguageByExtension(
  registry: readonly LanguageDefinition[],
  ext: string,
): LanguageDefinition | null {
  const normalized = ext.startsWith('.') ? ext : `.${ext}`;
  return registry.find((l) => l.extensions.includes(normalized)) ?? null;
}

/**
 * Look up a language by filename (e.g. "Dockerfile", "Makefile").
 */
export function getLanguageByFilename(
  registry: readonly LanguageDefinition[],
  filename: string,
): LanguageDefinition | null {
  const basename = filename.includes('/') ? filename.split('/').pop()! : filename;
  return registry.find((l) => l.filenames.includes(basename)) ?? null;
}

/**
 * Look up a language by MIME type.
 */
export function getLanguageByMimeType(
  registry: readonly LanguageDefinition[],
  mimeType: string,
): LanguageDefinition | null {
  return registry.find((l) => l.mimeTypes.includes(mimeType)) ?? null;
}

/**
 * Look up a language by alias (case-insensitive).
 */
export function getLanguageByAlias(
  registry: readonly LanguageDefinition[],
  alias: string,
): LanguageDefinition | null {
  const lower = alias.toLowerCase();
  return registry.find((l) =>
    l.aliases.some((a) => a.toLowerCase() === lower),
  ) ?? null;
}

/**
 * Look up a language by inspecting the first line of content (e.g. shebang or XML declaration).
 */
export function getLanguageByFirstLine(
  registry: readonly LanguageDefinition[],
  firstLine: string,
): LanguageDefinition | null {
  for (const lang of registry) {
    if (lang.firstLine !== undefined) {
      try {
        const regex = new RegExp(lang.firstLine);
        if (regex.test(firstLine)) return lang;
      } catch {
        continue;
      }
    }
  }

  const shebangLang = detectLanguageFromShebang(firstLine);
  if (shebangLang !== null) {
    const found = registry.find((l) => l.id === shebangLang);
    if (found !== undefined) return found;
  }

  return null;
}

// ═══ §3 LANGUAGE DETECTION — path, content, and shebang heuristics ═══

/**
 * Detect a language from a file path using extension and filename matching.
 */
export function detectLanguageFromPath(
  path: string,
  registry: readonly LanguageDefinition[],
): LanguageDefinition | null {
  const segments = path.split('/');
  const basename = segments[segments.length - 1] ?? '';

  const byFilename = getLanguageByFilename(registry, basename);
  if (byFilename !== null) return byFilename;

  const dotIdx = basename.lastIndexOf('.');
  if (dotIdx >= 0) {
    const ext = basename.slice(dotIdx);
    const byExt = getLanguageByExtension(registry, ext);
    if (byExt !== null) return byExt;

    const extKey = ext.toLowerCase();
    if (EXTENSION_MAP[extKey] !== undefined) {
      const langId = asLanguageId(EXTENSION_MAP[extKey]);
      const found = registry.find((l) => l.id === langId);
      if (found !== undefined) return found;
    }
  }

  return null;
}

/**
 * Detect a language from file content using first-line inspection and heuristics.
 */
export function detectLanguageFromContent(
  content: string,
  registry: readonly LanguageDefinition[],
): LanguageDefinition | null {
  const firstLine = content.split('\n')[0] ?? '';

  const byFirstLine = getLanguageByFirstLine(registry, firstLine);
  if (byFirstLine !== null) return byFirstLine;

  if (firstLine.startsWith('<?xml')) {
    return registry.find((l) => (l.id as string) === 'xml') ?? null;
  }
  if (firstLine.startsWith('<!DOCTYPE html') || firstLine.startsWith('<html')) {
    return registry.find((l) => (l.id as string) === 'html') ?? null;
  }
  if (content.includes('import ') && content.includes('from ') && (content.includes(': ') || content.includes('interface '))) {
    return registry.find((l) => (l.id as string) === 'typescript') ?? null;
  }
  if (content.includes('def ') && content.includes(':') && !content.includes('{')) {
    return registry.find((l) => (l.id as string) === 'python') ?? null;
  }
  if (content.includes('fn ') && content.includes('->') && content.includes('let ')) {
    return registry.find((l) => (l.id as string) === 'rust') ?? null;
  }
  if (content.includes('func ') && content.includes('package ')) {
    return registry.find((l) => (l.id as string) === 'go') ?? null;
  }

  return null;
}

/**
 * Detect a language from a shebang line (e.g. #!/usr/bin/env python3).
 */
export function detectLanguageFromShebang(firstLine: string): LanguageId | null {
  if (!firstLine.startsWith('#!')) return null;

  const line = firstLine.slice(2).trim();
  const parts = line.split(/\s+/);
  const executable = parts[parts.length - 1] ?? '';
  const base = executable.includes('/') ? executable.split('/').pop()! : executable;

  const cleanBase = base.replace(/[0-9.]+$/, '');

  if (SHEBANG_MAP[cleanBase] !== undefined) {
    return asLanguageId(SHEBANG_MAP[cleanBase]);
  }
  if (SHEBANG_MAP[base] !== undefined) {
    return asLanguageId(SHEBANG_MAP[base]);
  }

  return null;
}

/** Mapping from shebang executable names to language IDs. */
export const SHEBANG_MAP: Record<string, string> = {
  'node': 'javascript',
  'nodejs': 'javascript',
  'deno': 'typescript',
  'bun': 'typescript',
  'python': 'python',
  'python3': 'python',
  'ruby': 'ruby',
  'perl': 'perl',
  'php': 'php',
  'bash': 'shell',
  'sh': 'shell',
  'zsh': 'shell',
  'fish': 'shell',
  'dash': 'shell',
  'lua': 'lua',
  'Rscript': 'r',
  'swift': 'swift',
  'elixir': 'elixir',
  'awk': 'shell',
  'sed': 'shell',
  'pwsh': 'shell',
  'groovy': 'groovy',
};

/** Common file extension to language ID mapping. */
export const EXTENSION_MAP: Record<string, string> = {
  '.ts': 'typescript', '.tsx': 'tsx', '.js': 'javascript', '.jsx': 'jsx',
  '.mjs': 'javascript', '.cjs': 'javascript', '.mts': 'typescript', '.cts': 'typescript',
  '.py': 'python', '.pyw': 'python', '.pyi': 'python',
  '.rs': 'rust', '.go': 'go', '.java': 'java',
  '.c': 'c', '.h': 'c', '.cpp': 'cpp', '.cxx': 'cpp', '.cc': 'cpp',
  '.hpp': 'cpp', '.hxx': 'cpp', '.hh': 'cpp',
  '.cs': 'csharp', '.rb': 'ruby', '.php': 'php',
  '.swift': 'swift', '.kt': 'kotlin', '.kts': 'kotlin', '.scala': 'scala',
  '.html': 'html', '.htm': 'html',
  '.css': 'css', '.scss': 'scss', '.less': 'less', '.sass': 'scss',
  '.json': 'json', '.jsonc': 'json', '.json5': 'json',
  '.yaml': 'yaml', '.yml': 'yaml',
  '.toml': 'toml', '.xml': 'xml', '.xsl': 'xml', '.xslt': 'xml',
  '.md': 'markdown', '.markdown': 'markdown', '.mdx': 'markdown',
  '.sql': 'sql', '.sh': 'shell', '.bash': 'shell', '.zsh': 'shell',
  '.fish': 'shell', '.ps1': 'shell', '.psm1': 'shell',
  '.dockerfile': 'dockerfile',
  '.graphql': 'graphql', '.gql': 'graphql',
  '.proto': 'proto', '.lua': 'lua', '.r': 'r', '.R': 'r',
  '.dart': 'dart', '.ex': 'elixir', '.exs': 'elixir',
  '.hs': 'haskell', '.lhs': 'haskell',
  '.ml': 'ocaml', '.mli': 'ocaml',
  '.zig': 'zig',
  '.svelte': 'svelte', '.vue': 'vue',
};

// ═══ §4 BUILT-IN LANGUAGES — definitions for common languages ═══

function makeLang(
  id: string,
  name: string,
  aliases: string[],
  extensions: string[],
  filenames: string[],
  mimeTypes: string[],
  config: LanguageConfiguration,
  firstLine?: string,
): LanguageDefinition {
  return {
    id: asLanguageId(id),
    name,
    aliases,
    extensions,
    filenames,
    mimeTypes,
    firstLine,
    configuration: config,
  };
}

const CLIKE_BRACKETS: BracketPair[] = [
  { open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' },
];
const CLIKE_AUTOCLOSE: AutoClosingPair[] = [
  { open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' },
  { open: "'", close: "'", notIn: ['string', 'comment'] },
  { open: '"', close: '"', notIn: ['string'] },
  { open: '`', close: '`', notIn: ['string', 'comment'] },
];
const CLIKE_SURROUND: SurroundingPair[] = [
  { open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' },
  { open: "'", close: "'" }, { open: '"', close: '"' }, { open: '`', close: '`' },
];
const CLIKE_INDENT: IndentationRules = {
  increaseIndentPattern: '(^.*\\{[^}]*$)',
  decreaseIndentPattern: '(^\\s*\\})',
};

function clikeConfig(lineComment: string = '//', blockComment: readonly [string, string] = ['/*', '*/']): LanguageConfiguration {
  return {
    comments: { lineComment, blockComment },
    brackets: CLIKE_BRACKETS,
    autoClosingPairs: CLIKE_AUTOCLOSE,
    surroundingPairs: CLIKE_SURROUND,
    folding: { markers: { start: '\\{', end: '\\}' } },
    indentation: CLIKE_INDENT,
    wordPattern: "(-?\\d*\\.\\d\\w*)|([^\\`\\~\\!\\@\\#\\%\\^\\&\\*\\(\\)\\-\\=\\+\\[\\{\\]\\}\\\\\\|\\;\\:\\'\\\",\\.\\<\\>\\/\\?\\s]+)",
  };
}

function scriptConfig(lineComment: string = '#'): LanguageConfiguration {
  return {
    comments: { lineComment },
    brackets: CLIKE_BRACKETS,
    autoClosingPairs: CLIKE_AUTOCLOSE,
    surroundingPairs: CLIKE_SURROUND,
    folding: { offSide: true },
    indentation: {
      increaseIndentPattern: '(:\\s*$)',
      decreaseIndentPattern: '(^\\s*(else|elif|except|finally|\\}))',
    },
  };
}

/** Built-in language definitions covering major languages. */
export const BUILTIN_LANGUAGES: readonly LanguageDefinition[] = [
  makeLang('typescript', 'TypeScript', ['ts'], ['.ts', '.mts', '.cts'], [], ['text/typescript', 'application/typescript'], clikeConfig()),
  makeLang('javascript', 'JavaScript', ['js'], ['.js', '.mjs', '.cjs'], [], ['text/javascript', 'application/javascript'], clikeConfig()),
  makeLang('python', 'Python', ['py'], ['.py', '.pyw', '.pyi'], [], ['text/x-python'], scriptConfig('#')),
  makeLang('rust', 'Rust', ['rs'], ['.rs'], [], ['text/x-rust'], clikeConfig('//', ['/*', '*/'])),
  makeLang('go', 'Go', ['golang'], ['.go'], [], ['text/x-go'], clikeConfig('//', ['/*', '*/'])),
  makeLang('java', 'Java', [], ['.java'], [], ['text/x-java'], clikeConfig()),
  makeLang('c', 'C', [], ['.c', '.h'], [], ['text/x-c'], clikeConfig()),
  makeLang('cpp', 'C++', ['c++', 'cxx'], ['.cpp', '.cxx', '.cc', '.hpp', '.hxx', '.hh'], [], ['text/x-c++'], clikeConfig()),
  makeLang('csharp', 'C#', ['cs'], ['.cs'], [], ['text/x-csharp'], clikeConfig()),
  makeLang('ruby', 'Ruby', ['rb'], ['.rb', '.rake', '.gemspec'], ['Gemfile', 'Rakefile'], ['text/x-ruby'], scriptConfig('#')),
  makeLang('php', 'PHP', [], ['.php'], [], ['text/x-php'], clikeConfig('//', ['/*', '*/'])),
  makeLang('swift', 'Swift', [], ['.swift'], [], ['text/x-swift'], clikeConfig()),
  makeLang('kotlin', 'Kotlin', ['kt'], ['.kt', '.kts'], [], ['text/x-kotlin'], clikeConfig()),
  makeLang('scala', 'Scala', [], ['.scala', '.sc'], [], ['text/x-scala'], clikeConfig()),
  makeLang('html', 'HTML', ['htm'], ['.html', '.htm'], [], ['text/html'], {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [{ open: '<', close: '>' }, ...CLIKE_BRACKETS],
    autoClosingPairs: [{ open: '<', close: '>' }, ...CLIKE_AUTOCLOSE],
    surroundingPairs: [{ open: '<', close: '>' }, ...CLIKE_SURROUND],
    folding: { markers: { start: '<!--\\s*#region', end: '<!--\\s*#endregion' } },
    indentation: { increaseIndentPattern: '<(?!area|base|br|col|embed|hr|img|input|link|meta|param)([a-zA-Z][^/]*?)(?<!/)>$', decreaseIndentPattern: '^\\s*</' },
  }),
  makeLang('css', 'CSS', [], ['.css'], [], ['text/css'], {
    comments: { blockComment: ['/*', '*/'] },
    brackets: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' }],
    autoClosingPairs: CLIKE_AUTOCLOSE,
    surroundingPairs: CLIKE_SURROUND,
    folding: { markers: { start: '\\{', end: '\\}' } },
    indentation: CLIKE_INDENT,
  }),
  makeLang('scss', 'SCSS', ['sass'], ['.scss', '.sass'], [], ['text/x-scss'], clikeConfig('//', ['/*', '*/'])),
  makeLang('less', 'Less', [], ['.less'], [], ['text/x-less'], clikeConfig('//', ['/*', '*/'])),
  makeLang('json', 'JSON', ['jsonc'], ['.json', '.jsonc', '.json5'], [], ['application/json'], {
    comments: {},
    brackets: [{ open: '{', close: '}' }, { open: '[', close: ']' }],
    autoClosingPairs: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '"', close: '"' }],
    surroundingPairs: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '"', close: '"' }],
    folding: { markers: { start: '\\{|\\[', end: '\\}|\\]' } },
    indentation: { increaseIndentPattern: '(\\{|\\[)\\s*$', decreaseIndentPattern: '^\\s*(\\}|\\])' },
  }),
  makeLang('yaml', 'YAML', ['yml'], ['.yaml', '.yml'], [], ['text/yaml', 'application/yaml'], scriptConfig('#')),
  makeLang('toml', 'TOML', [], ['.toml'], ['Cargo.toml'], ['text/x-toml'], scriptConfig('#')),
  makeLang('xml', 'XML', ['xsl', 'xslt'], ['.xml', '.xsl', '.xslt', '.svg', '.xsd'], [], ['text/xml', 'application/xml'], {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [{ open: '<', close: '>' }],
    autoClosingPairs: [{ open: '<', close: '>' }, { open: '"', close: '"' }, { open: "'", close: "'" }],
    surroundingPairs: [{ open: '<', close: '>' }, { open: '"', close: '"' }],
    folding: { markers: { start: '<[^/]', end: '</|/>' } },
    indentation: { increaseIndentPattern: '<[^?!/][^>]*[^/]>\\s*$', decreaseIndentPattern: '^\\s*</' },
  }),
  makeLang('markdown', 'Markdown', ['md'], ['.md', '.markdown', '.mdx'], [], ['text/markdown'], {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [{ open: '(', close: ')' }, { open: '[', close: ']' }],
    autoClosingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '`', close: '`' }, { open: '"', close: '"' }],
    surroundingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '*', close: '*' }, { open: '_', close: '_' }, { open: '`', close: '`' }],
    folding: { offSide: true },
    indentation: { increaseIndentPattern: '^\\s*[-*+]\\s', decreaseIndentPattern: '^$' },
  }),
  makeLang('sql', 'SQL', [], ['.sql'], [], ['text/x-sql'], {
    comments: { lineComment: '--', blockComment: ['/*', '*/'] },
    brackets: [{ open: '(', close: ')' }],
    autoClosingPairs: [{ open: '(', close: ')' }, { open: "'", close: "'" }, { open: '"', close: '"' }],
    surroundingPairs: [{ open: '(', close: ')' }, { open: "'", close: "'" }, { open: '"', close: '"' }],
    folding: { markers: { start: 'BEGIN|\\(', end: 'END|\\)' } },
    indentation: { increaseIndentPattern: '(BEGIN|\\()\\s*$', decreaseIndentPattern: '^\\s*(END|\\))' },
  }),
  makeLang('shell', 'Shell', ['bash', 'sh', 'zsh'], ['.sh', '.bash', '.zsh', '.fish', '.ps1'], [], ['text/x-shellscript'], scriptConfig('#')),
  makeLang('dockerfile', 'Dockerfile', ['docker'], ['.dockerfile'], ['Dockerfile', 'Dockerfile.dev', 'Dockerfile.prod'], ['text/x-dockerfile'], scriptConfig('#')),
  makeLang('graphql', 'GraphQL', ['gql'], ['.graphql', '.gql'], [], ['application/graphql'], scriptConfig('#')),
  makeLang('proto', 'Protocol Buffers', ['protobuf'], ['.proto'], [], ['text/x-protobuf'], clikeConfig()),
  makeLang('lua', 'Lua', [], ['.lua'], [], ['text/x-lua'], {
    comments: { lineComment: '--', blockComment: ['--[[', ']]'] },
    brackets: CLIKE_BRACKETS,
    autoClosingPairs: CLIKE_AUTOCLOSE,
    surroundingPairs: CLIKE_SURROUND,
    folding: { markers: { start: 'function|do|then', end: 'end' } },
    indentation: { increaseIndentPattern: '(function|do|then|else|elseif)\\s*$', decreaseIndentPattern: '^\\s*(end|else|elseif)' },
  }),
  makeLang('r', 'R', ['R'], ['.r', '.R', '.rmd'], [], ['text/x-r'], scriptConfig('#')),
  makeLang('dart', 'Dart', [], ['.dart'], [], ['text/x-dart'], clikeConfig()),
  makeLang('elixir', 'Elixir', ['ex'], ['.ex', '.exs'], [], ['text/x-elixir'], scriptConfig('#')),
  makeLang('haskell', 'Haskell', ['hs'], ['.hs', '.lhs'], [], ['text/x-haskell'], {
    comments: { lineComment: '--', blockComment: ['{-', '-}'] },
    brackets: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '{', close: '}' }],
    autoClosingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '{', close: '}' }, { open: '"', close: '"' }],
    surroundingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '"', close: '"' }],
    folding: { offSide: true },
    indentation: { increaseIndentPattern: '(where|let|do|of|\\{)\\s*$', decreaseIndentPattern: '^\\s*(in|\\})' },
  }),
  makeLang('ocaml', 'OCaml', ['ml'], ['.ml', '.mli'], [], ['text/x-ocaml'], {
    comments: { blockComment: ['(*', '*)'] },
    brackets: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '{', close: '}' }],
    autoClosingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '{', close: '}' }, { open: '"', close: '"' }],
    surroundingPairs: [{ open: '(', close: ')' }, { open: '[', close: ']' }, { open: '"', close: '"' }],
    folding: { markers: { start: 'struct|sig|begin', end: 'end' } },
    indentation: { increaseIndentPattern: '(struct|sig|begin|do|then|else)\\s*$', decreaseIndentPattern: '^\\s*(end|done)' },
  }),
  makeLang('zig', 'Zig', [], ['.zig'], [], ['text/x-zig'], clikeConfig()),
  makeLang('svelte', 'Svelte', [], ['.svelte'], [], ['text/x-svelte'], {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' }, { open: '<', close: '>' }],
    autoClosingPairs: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' }, { open: '<', close: '>' }, { open: "'", close: "'" }, { open: '"', close: '"' }, { open: '`', close: '`' }],
    surroundingPairs: CLIKE_SURROUND,
    folding: { markers: { start: '<|\\{', end: '>|\\}' } },
    indentation: CLIKE_INDENT,
  }),
  makeLang('vue', 'Vue', [], ['.vue'], [], ['text/x-vue'], {
    comments: { blockComment: ['<!--', '-->'] },
    brackets: [{ open: '{', close: '}' }, { open: '[', close: ']' }, { open: '(', close: ')' }, { open: '<', close: '>' }],
    autoClosingPairs: CLIKE_AUTOCLOSE,
    surroundingPairs: CLIKE_SURROUND,
    folding: { markers: { start: '<|\\{', end: '>|\\}' } },
    indentation: CLIKE_INDENT,
  }),
  makeLang('jsx', 'JSX', [], ['.jsx'], [], ['text/jsx'], clikeConfig()),
  makeLang('tsx', 'TSX', [], ['.tsx'], [], ['text/tsx'], clikeConfig()),
];

// ═══ §5 CONFIGURATION — accessors for language configuration fields ═══

/**
 * Get the comment rules for a language.
 */
export function getCommentRule(language: LanguageDefinition): CommentRule {
  return language.configuration.comments;
}

/**
 * Get the bracket pairs for a language.
 */
export function getBracketPairs(language: LanguageDefinition): BracketPair[] {
  return [...language.configuration.brackets];
}

/**
 * Get the auto-closing pairs for a language.
 */
export function getAutoClosingPairs(language: LanguageDefinition): AutoClosingPair[] {
  return [...language.configuration.autoClosingPairs];
}

/**
 * Get the surrounding pairs for a language.
 */
export function getSurroundingPairs(language: LanguageDefinition): SurroundingPair[] {
  return [...language.configuration.surroundingPairs];
}

/**
 * Get the folding rules for a language.
 */
export function getFoldingRules(language: LanguageDefinition): FoldingRules {
  return language.configuration.folding;
}

/**
 * Get the indentation rules for a language.
 */
export function getIndentationRules(language: LanguageDefinition): IndentationRules {
  return language.configuration.indentation;
}

/**
 * Get the word pattern for a language.
 */
export function getWordPattern(language: LanguageDefinition): string | undefined {
  return language.configuration.wordPattern;
}

/**
 * Get the onEnterRules for a language.
 */
export function getOnEnterRules(language: LanguageDefinition): OnEnterRule[] {
  return language.configuration.onEnterRules ? [...language.configuration.onEnterRules] : [];
}

// ═══ §6 LANGUAGE FEATURES — capability queries and embedding ═══

export type LanguageFeature = 'comments' | 'brackets' | 'folding' | 'indentation' | 'snippets';

/**
 * Check whether a language supports a given feature.
 */
export function supportsFeature(language: LanguageDefinition, feature: LanguageFeature): boolean {
  const config = language.configuration;
  switch (feature) {
    case 'comments':
      return config.comments.lineComment !== undefined || config.comments.blockComment !== undefined;
    case 'brackets':
      return config.brackets.length > 0;
    case 'folding':
      return config.folding.offSide === true || config.folding.markers !== undefined;
    case 'indentation':
      return config.indentation.increaseIndentPattern !== '' || config.indentation.decreaseIndentPattern !== '';
    case 'snippets':
      return true; // All languages support snippets
    default:
      return false;
  }
}

/**
 * Get the language hierarchy for embedded languages.
 * For example, HTML can embed CSS and JavaScript.
 */
export function getLanguageHierarchy(
  languageId: LanguageId,
  registry: readonly LanguageDefinition[],
): LanguageId[] {
  const hierarchy: LanguageId[] = [languageId];
  const id = languageId as string;

  const embeddingMap: Record<string, string[]> = {
    'html': ['css', 'javascript'],
    'svelte': ['css', 'javascript', 'typescript'],
    'vue': ['css', 'javascript', 'typescript', 'html'],
    'jsx': ['javascript', 'html'],
    'tsx': ['typescript', 'html'],
    'markdown': ['javascript', 'typescript', 'python', 'rust', 'go', 'java', 'css', 'html', 'shell', 'sql', 'json', 'yaml'],
    'php': ['html', 'css', 'javascript'],
  };

  const embedded = embeddingMap[id];
  if (embedded !== undefined) {
    for (const embId of embedded) {
      const langId = asLanguageId(embId);
      const found = registry.find((l) => l.id === langId);
      if (found !== undefined) {
        hierarchy.push(langId);
      }
    }
  }

  return hierarchy;
}

/**
 * Check whether a language supports embedding other languages.
 */
export function languageSupportsEmbedding(languageId: LanguageId): boolean {
  const id = languageId as string;
  return ['html', 'svelte', 'vue', 'jsx', 'tsx', 'markdown', 'php'].includes(id);
}

/**
 * Detect embedded language ranges within content of a host language.
 * Returns ranges paired with the detected embedded language.
 */
export function getEmbeddedLanguages(
  languageId: LanguageId,
  content: string,
): Array<{ languageId: LanguageId; range: Range }> {
  const id = languageId as string;
  const results: Array<{ languageId: LanguageId; range: Range }> = [];

  if (id === 'html' || id === 'svelte' || id === 'vue' || id === 'php') {
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/g;
    let match: RegExpExecArray | null;
    while ((match = styleRegex.exec(content)) !== null) {
      const tagEnd = content.indexOf('>', match.index) + 1;
      const closeStart = content.indexOf('</style>', tagEnd);
      if (closeStart >= 0) {
        const startPos = offsetToPos(content, tagEnd);
        const endPos = offsetToPos(content, closeStart);
        const langAttr = match[0].match(/lang="([^"]+)"/);
        const embeddedLang = langAttr ? langAttr[1]! : 'css';
        results.push({
          languageId: asLanguageId(embeddedLang),
          range: { start: startPos, end: endPos },
        });
      }
    }

    const scriptRegex = /<script[^>]*>([\s\S]*?)<\/script>/g;
    while ((match = scriptRegex.exec(content)) !== null) {
      const tagEnd = content.indexOf('>', match.index) + 1;
      const closeStart = content.indexOf('</script>', tagEnd);
      if (closeStart >= 0) {
        const startPos = offsetToPos(content, tagEnd);
        const endPos = offsetToPos(content, closeStart);
        const langAttr = match[0].match(/lang="([^"]+)"/);
        const embeddedLang = langAttr ? langAttr[1]! : 'javascript';
        results.push({
          languageId: asLanguageId(embeddedLang),
          range: { start: startPos, end: endPos },
        });
      }
    }
  }

  if (id === 'markdown') {
    const fenceRegex = /^```(\w+)\s*$/gm;
    let match: RegExpExecArray | null;
    while ((match = fenceRegex.exec(content)) !== null) {
      const lang = match[1];
      const codeStart = match.index + match[0].length + 1;
      const endFence = content.indexOf('\n```', codeStart);
      if (endFence >= 0) {
        results.push({
          languageId: asLanguageId(lang!),
          range: {
            start: offsetToPos(content, codeStart),
            end: offsetToPos(content, endFence),
          },
        });
      }
    }
  }

  return results;
}

// ═══ INTERNAL HELPERS ═══

function offsetToPos(content: string, offset: number): { line: number; column: number } {
  let line = 0;
  let lastNewline = -1;
  for (let i = 0; i < offset && i < content.length; i++) {
    if (content[i] === '\n') {
      line++;
      lastNewline = i;
    }
  }
  return { line, column: offset - lastNewline - 1 };
}
